import React, { useState } from 'react';
import { reviewAPI } from '../services/api';
import StarRating from './StarRating';
import './ReviewForm.css';

const ReviewForm = ({ booking, onReviewSubmitted, onCancel }) => {
  const [formData, setFormData] = useState({
    rating: 0,
    comment: '',
    serviceQuality: 0,
    punctuality: 0,
    communication: 0,
    wouldRecommend: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleRatingChange = (field, rating) => {
    setFormData(prev => ({ ...prev, [field]: rating }));
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.rating === 0) {
      setError('Please select an overall rating');
      return;
    }

    if (formData.serviceQuality === 0) {
      setError('Please rate the service quality');
      return;
    }

    if (formData.punctuality === 0) {
      setError('Please rate the punctuality');
      return;
    }

    if (formData.communication === 0) {
      setError('Please rate the communication');
      return;
    }

    if (formData.comment.trim().length < 10) {
      setError('Please write at least 10 characters in your review');
      return;
    }

    setIsSubmitting(true);

    try {
      const reviewData = {
        bookingId: booking._id,
        rating: formData.rating,
        comment: formData.comment.trim(),
        serviceQuality: formData.serviceQuality,
        punctuality: formData.punctuality,
        communication: formData.communication,
        wouldRecommend: formData.wouldRecommend
      };

      await reviewAPI.createReview(reviewData);
      
      alert('Thank you for your review! It helps other customers make better decisions.');
      
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
      
    } catch (err) {
      setError(err.message || 'Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getServiceIcon = (service) => {
    const icons = {
      'sweeping-mopping': '🧹',
      'cooking': '👨‍🍳',
      'laundry': '👕',
      'gardening': '🌱',
      'pet-care': '🐕',
      'elderly-care': '👴',
      'babysitting': '👶',
      'hair-dresser': '✂️',
      'other': '🔧'
    };
    return icons[service] || '🔧';
  };

  // Calculate duration for display
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return booking.duration || 0;
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return (endMinutes - startMinutes) / 60;
  };

  return (
    <div className="review-form-overlay">
      <div className="review-form-container">
        <div className="review-form-header">
          <h2>✍️ Write a Review</h2>
          <p>Share your experience with {booking.helperId.name}</p>
        </div>

        {/* Booking Info Summary */}
        <div className="booking-summary">
          <div className="service-info">
            <span className="service-icon">{getServiceIcon(booking.service)}</span>
            <div className="service-details">
              <h4>{booking.service.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</h4>
              <p>{new Date(booking.date).toLocaleDateString()} • {calculateDuration(booking.startTime, booking.endTime)} hours</p>
            </div>
          </div>
          <div className="helper-info">
            <div className="helper-avatar">
              {booking.helperId.name.charAt(0).toUpperCase()}
            </div>
            <div className="helper-details">
              <h4>{booking.helperId.name}</h4>
              <p>{booking.helperId.location}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="review-form">
          {error && <div className="error-message">{error}</div>}

          {/* Rating Selection */}
          <div className="form-group">
            <label className="form-label">Overall Rating *</label>
            <div className="rating-input">
              <StarRating
                rating={formData.rating}
                onRatingChange={(rating) => handleRatingChange('rating', rating)}
                size="large"
                readonly={false}
                showText={true}
              />
            </div>
            <small className="form-hint">Click on the stars to rate your experience</small>
          </div>

          {/* Service Quality */}
          <div className="form-group">
            <label className="form-label">Service Quality *</label>
            <div className="rating-input">
              <StarRating
                rating={formData.serviceQuality}
                onRatingChange={(rating) => handleRatingChange('serviceQuality', rating)}
                size="large"
                readonly={false}
                showText={true}
              />
            </div>
            <small className="form-hint">Click on the stars to rate the service quality</small>
          </div>

          {/* Punctuality */}
          <div className="form-group">
            <label className="form-label">Punctuality *</label>
            <div className="rating-input">
              <StarRating
                rating={formData.punctuality}
                onRatingChange={(rating) => handleRatingChange('punctuality', rating)}
                size="large"
                readonly={false}
                showText={true}
              />
            </div>
            <small className="form-hint">Click on the stars to rate the punctuality</small>
          </div>

          {/* Communication */}
          <div className="form-group">
            <label className="form-label">Communication *</label>
            <div className="rating-input">
              <StarRating
                rating={formData.communication}
                onRatingChange={(rating) => handleRatingChange('communication', rating)}
                size="large"
                readonly={false}
                showText={true}
              />
            </div>
            <small className="form-hint">Click on the stars to rate the communication</small>
          </div>

          {/* Review Comment */}
          <div className="form-group">
            <label htmlFor="comment" className="form-label">
              Your Review * 
              <span className="char-count">({formData.comment.length}/500)</span>
            </label>
            <textarea
              id="comment"
              name="comment"
              value={formData.comment}
              onChange={handleInputChange}
              placeholder="Tell others about your experience. What did you like? How was the service quality? Would you book again?"
              className="form-textarea"
              rows={5}
              maxLength={500}
              required
            />
            <small className="form-hint">Minimum 10 characters required</small>
          </div>

          {/* Recommendation */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="wouldRecommend"
                checked={formData.wouldRecommend}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="checkmark">✅</span>
              I would recommend this helper to others
            </label>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={onCancel}
              className="btn-cancel"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting || formData.rating === 0 || formData.serviceQuality === 0 || formData.punctuality === 0 || formData.communication === 0}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Submitting...
                </>
              ) : (
                'Submit Review'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewForm; 